/**
 * O <svg> de fora de todo gráfico. Na tela, é só o gráfico. Na imagem para
 * WhatsApp (quando recebe `cabecalho`), acrescenta título, subtítulo, notas
 * de rodapé, fundo, a assinatura "Metas Aliança" e a logo (se tiver) no canto.
 *
 * Lê: nada.
 * Usado por: graficos/GraficoBarras.tsx e graficos/CorridaCarrinhos.tsx.
 */
import type { ReactNode } from 'react';
import type { Cores } from './cores.ts';
import { FONTE, quebrarTexto } from './svg.ts';

export interface Cabecalho {
  titulo: string;
  subtitulo?: string;
  rodape?: string[];
  /** Ex.: "Gerado em 12/10/2026 14:30". */
  assinatura?: string;
  /** Endereço da logo (Cadastros → Logo): aparece no canto de cima, à direita. */
  logo?: string | null;
}

const LOGO_LARGURA = 170;
const LOGO_ALTURA = 52;

const MARGEM = 32;

interface Props {
  largura: number;
  /** Altura do gráfico em si (sem cabeçalho e rodapé). */
  altura: number;
  cores: Cores;
  descricao: string;
  cabecalho?: Cabecalho;
  children: ReactNode;
}

export function Moldura({ largura, altura, cores, descricao, cabecalho, children }: Props) {
  if (!cabecalho) {
    return (
      <svg width={largura} height={altura} viewBox={`0 0 ${largura} ${altura}`} role="img" aria-label={descricao} fontFamily={FONTE}>
        {children}
      </svg>
    );
  }

  const larguraTexto = largura - MARGEM * 2;
  const linhasSubtitulo = cabecalho.subtitulo ? quebrarTexto(cabecalho.subtitulo, larguraTexto, 17) : [];
  const linhasRodape = (cabecalho.rodape ?? []).flatMap((texto) => quebrarTexto(texto, larguraTexto, 14));
  const alturaTopo = MARGEM + 34 + linhasSubtitulo.length * 24 + 22;
  const alturaRodape = 18 + linhasRodape.length * 20 + (cabecalho.assinatura ? 26 : 0) + MARGEM;
  const total = alturaTopo + altura + alturaRodape;

  return (
    <svg width={largura} height={total} viewBox={`0 0 ${largura} ${total}`} role="img" aria-label={descricao} fontFamily={FONTE}>
      <rect width={largura} height={total} fill={cores.superficie} />
      {cabecalho.logo ? (
        <image href={cabecalho.logo} x={largura - MARGEM - LOGO_LARGURA} y={MARGEM - 6} width={LOGO_LARGURA} height={LOGO_ALTURA} preserveAspectRatio="xMaxYMin meet" />
      ) : null}
      <text x={MARGEM} y={MARGEM + 28} fontSize={30} fontWeight={700} fill={cores.tinta}>
        {cabecalho.titulo}
      </text>
      {linhasSubtitulo.map((linha, i) => (
        <text key={i} x={MARGEM} y={MARGEM + 60 + i * 24} fontSize={17} fill={cores.tinta2}>
          {linha}
        </text>
      ))}
      <rect x={MARGEM} y={alturaTopo - 12} width={larguraTexto} height={2} fill={cores.barra} />
      <g transform={`translate(0, ${alturaTopo})`}>{children}</g>
      {linhasRodape.map((linha, i) => (
        <text key={i} x={MARGEM} y={alturaTopo + altura + 28 + i * 20} fontSize={14} fill={cores.tinta3}>
          {linha}
        </text>
      ))}
      {cabecalho.assinatura ? (
        <text x={largura - MARGEM} y={total - MARGEM + 6} fontSize={13} textAnchor="end" fill={cores.tinta3}>
          {cabecalho.assinatura}
        </text>
      ) : null}
    </svg>
  );
}
